import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortBy } from 'lodash';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MenubarSubmenu } from '../../../../components/Menubar/MenubarSubmenu';
import { MenubarItem } from '../../../../components/Menubar/MenubarItem';
import { availableLanguages, languageKeyToLabel } from '../../../../i18n';
import { getConfig } from '../../../../utils/getConfig';
import { parseBoolean } from '../../../../utils/parseStringToType';
import { showToast } from '../../actions/toast';
import { setLanguage } from '../../actions/preferences';
import { Menubar } from '../../../../components/Menubar/Menubar';
import CaretLeftIcon from '../../../../images/left-arrow.svg';
// Imported as URLs (?url bypasses vite-plugin-svgr) and rendered via <img> so
// the nav's themed `g, path { fill }` rules can't recolor the multi-color
// wordmark — an inlined SVGR component would inherit those fills and lose its
// green.
import logoHorizontal from '../../../../images/stemarts/horizontal.svg?url';
import logoHorizontalWhite from '../../../../images/stemarts/horizontal-white.svg?url';
import { selectRootFile } from '../../selectors/files';
import { selectSketchPath } from '../../selectors/project';
import { metaKey, metaKeyName } from '../../../../utils/metaKey';
import { useSketchActions } from '../../hooks';
import {
  getAuthenticated,
  getIsUserOwner,
  getIsStaff,
  getIsAdmin
} from '../../selectors/users';
import { cloneProject, saveToExamples } from '../../actions/project';
import {
  newFile,
  newFolder,
  showKeyboardShortcutModal,
  openStarsModal,
  runSketch,
  stopSketch,
  openCreateClassroomModal,
  openManageJoinCodeModal,
  openAddRoleModal,
  openPublishClassroomModal,
  startJoinFlow,
  resumePendingJoin
} from '../../actions/ide';
import { logoutUser } from '../../../User/actions';
import { CmControllerContext } from '../../pages/IDEView';
import MobileNav from './MobileNav';
import ClassroomStudentsMenu from './ClassroomStudentsMenu';
import MyClassroomMenu from './MyClassroomMenu';
import AddHelpersMenu from './AddHelpersMenu';
import useIsMobile from '../../hooks/useIsMobile';

const Nav = ({ layout }) => {
  const isMobile = useIsMobile();

  return isMobile ? (
    <MobileNav />
  ) : (
    <>
      <header className="nav__header">
        <div className="nav__item-logo">
          <Logo />
        </div>

        <nav className="nav">
          <Menubar>
            <LeftLayout layout={layout} />
            <UserMenu />
          </Menubar>
        </nav>
      </header>
    </>
  );
};

Nav.propTypes = {
  layout: PropTypes.oneOf(['dashboard', 'project'])
};

Nav.defaultProps = {
  layout: 'project'
};

const LeftLayout = (props) => {
  switch (props.layout) {
    case 'dashboard':
      return <DashboardMenu />;
    case 'project':
    default:
      return <ProjectMenu />;
  }
};

LeftLayout.propTypes = {
  layout: PropTypes.oneOf(['dashboard', 'project'])
};

LeftLayout.defaultProps = {
  layout: 'project'
};

const isLoginEnabled = parseBoolean(getConfig('LOGIN_ENABLED'), true);

const UserMenu = () => {
  const isAuthenticated = useSelector(getAuthenticated);

  if (isLoginEnabled && isAuthenticated) {
    return <AuthenticatedUserMenu />;
  } else if (isLoginEnabled && !isAuthenticated) {
    return <UnauthenticatedUserMenu />;
  }

  return null;
};

const Logo = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const theme = useSelector((state) => state.preferences.theme);
  // Dark and high-contrast themes get the white wordmark; light gets the
  // full-color one.
  const logoSrc = theme === 'light' ? logoHorizontal : logoHorizontalWhite;

  const mark = (
    <img src={logoSrc} alt={t('Common.p5logoARIA')} className="svg__logo" />
  );

  if (user?.username) {
    return <Link to={`/${user.username}/sketches`}>{mark}</Link>;
  }

  return <a href="https://p5js.org">{mark}</a>;
};

const DashboardMenu = () => {
  const { t } = useTranslation();
  const editorLink = useSelector(selectSketchPath);
  return (
    <ul className="nav__items-left" role="group">
      <li className="nav__item nav__item--no-icon">
        <Link to={editorLink} className="nav__back-link">
          <CaretLeftIcon
            className="nav__back-icon"
            focusable="false"
            aria-hidden="true"
          />
          <span className="nav__item-header">{t('Nav.BackEditor')}</span>
        </Link>
      </li>
    </ul>
  );
};

const ProjectMenu = () => {
  const isUserOwner = useSelector(getIsUserOwner);
  const project = useSelector((state) => state.project);
  const user = useSelector((state) => state.user);
  const isStaff = useSelector(getIsStaff);
  const isAdmin = useSelector(getIsAdmin);
  // Opened via an edit-capability share link → a non-owner may save (routed through the function).
  const canSaveShared = useSelector(
    (state) => state.ide.shareCapability === 'edit'
  );

  const dispatch = useDispatch();

  // After a signup→verify→login round-trip triggered by "Join classroom" while signed out, re-open
  // the join dialog with the pending code. resumePendingJoin no-ops unless a join was stashed.
  useEffect(() => {
    if (user.authenticated && user.verified === 'verified') {
      dispatch(resumePendingJoin());
    }
  }, [user.authenticated, user.verified, dispatch]);

  const isUnsaved = !project?.id;

  const rootFile = useSelector(selectRootFile);

  const cmRef = useContext(CmControllerContext);

  const { t } = useTranslation();
  const {
    newSketch,
    saveSketch,
    downloadSketch,
    shareSketch
  } = useSketchActions();

  const replaceCommand =
    metaKey === 'Ctrl' ? `${metaKeyName}+H` : `${metaKeyName}+⌥+F`;
  const newFileCommand =
    metaKey === 'Ctrl' ? `${metaKeyName}+Alt+N` : `${metaKeyName}+⌥+N`;

  return (
    <ul className="nav__items-left" role="group">
      <MenubarSubmenu id="file" title={t('Nav.File.Title')}>
        <MenubarItem id="file-new" onClick={newSketch}>
          {t('Nav.File.New')}
        </MenubarItem>
        <MenubarItem
          id="file-save"
          isDisabled={
            !user.authenticated ||
            !isLoginEnabled ||
            (!!project?.owner && !isUserOwner && !canSaveShared && !isStaff)
          }
          tooltipContent={
            !user.authenticated || !isLoginEnabled
              ? t('Nav.File.SaveTooltipUnauthenticated')
              : undefined
          }
          onClick={() => saveSketch(cmRef.current)}
        >
          {t('Common.Save')}
          <span className="nav__keyboard-shortcut">{metaKeyName}+S</span>
        </MenubarItem>
        <MenubarItem
          id="file-duplicate"
          isDisabled={!user.authenticated || isUnsaved}
          tooltipContent={
            !user.authenticated
              ? t('Nav.File.DuplicateTooltipUnauthenticated')
              : undefined
          }
          onClick={() => dispatch(cloneProject())}
        >
          {t('Nav.File.Duplicate')}
        </MenubarItem>
        {isAdmin && (
          <MenubarItem
            id="file-save-examples"
            isDisabled={isUnsaved || !isUserOwner}
            tooltipContent={
              isUnsaved || !isUserOwner
                ? t('Nav.File.SaveToExamplesTooltip', {
                    defaultValue:
                      'Save the sketch and open one you own to publish it as an example'
                  })
                : undefined
            }
            onClick={() => dispatch(saveToExamples())}
          >
            {t('Nav.File.SaveToExamples', {
              defaultValue: 'Save to examples'
            })}
          </MenubarItem>
        )}
        <MenubarItem
          id="file-share"
          isDisabled={isUnsaved}
          tooltipContent={
            isUnsaved ? t('Nav.File.ShareTooltipUnsaved') : undefined
          }
          onClick={shareSketch}
        >
          {t('Nav.File.Share')}
        </MenubarItem>
        <MenubarItem
          id="file-download"
          isDisabled={isUnsaved}
          tooltipContent={
            isUnsaved ? t('Nav.File.DownloadTooltipUnsaved') : undefined
          }
          onClick={downloadSketch}
        >
          {t('Nav.File.Download')}
        </MenubarItem>
        <MenubarItem
          id="file-open"
          isDisabled={!user.authenticated}
          tooltipContent={
            !user.authenticated
              ? t('Nav.File.OpenTooltipUnauthenticated')
              : undefined
          }
          href={`/${user.username}/sketches`}
        >
          {t('Nav.File.Open')}
        </MenubarItem>
        {/* Curated AR examples — the /examples page is public, so no auth gate. */}
        <MenubarItem id="file-ar-examples" href="/examples">
          {t('Nav.File.ARExamples', { defaultValue: 'AR Examples' })}
        </MenubarItem>
      </MenubarSubmenu>
      <MenubarSubmenu id="edit" title={t('Nav.Edit.Title')}>
        <MenubarItem id="edit-tidy" onClick={cmRef.current?.tidyCode}>
          {t('Nav.Edit.TidyCode')}
          <span className="nav__keyboard-shortcut">{metaKeyName}+Shift+F</span>
        </MenubarItem>
        <MenubarItem id="edit-find" onClick={cmRef.current?.showFind}>
          {t('Nav.Edit.Find')}
          <span className="nav__keyboard-shortcut">{metaKeyName}+F</span>
        </MenubarItem>
        <MenubarItem id="edit-replace" onClick={cmRef.current?.showReplace}>
          {t('Nav.Edit.Replace')}
          <span className="nav__keyboard-shortcut">{replaceCommand}</span>
        </MenubarItem>
      </MenubarSubmenu>
      <MenubarSubmenu id="sketch" title={t('Nav.Sketch.Title')}>
        <MenubarItem
          id="sketch-add-file"
          onClick={() => dispatch(newFile(rootFile.id))}
        >
          {t('Nav.Sketch.AddFile')}
          <span className="nav__keyboard-shortcut">{newFileCommand}</span>
        </MenubarItem>
        <MenubarItem
          id="sketch-add-folder"
          onClick={() => dispatch(newFolder(rootFile.id))}
        >
          {t('Nav.Sketch.AddFolder')}
        </MenubarItem>
        <MenubarItem
          id="sketch-constellations"
          onClick={() => dispatch(openStarsModal())}
        >
          {t('Nav.Sketch.Constellations', {
            defaultValue: 'Add Constellations…'
          })}
        </MenubarItem>
        <AddHelpersMenu />
        <MenubarItem id="sketch-run" onClick={() => dispatch(runSketch())}>
          {t('Nav.Sketch.Run')}
          <span className="nav__keyboard-shortcut">{metaKeyName}+Enter</span>
        </MenubarItem>
        <MenubarItem id="sketch-stop" onClick={() => dispatch(stopSketch())}>
          {t('Nav.Sketch.Stop')}
          <span className="nav__keyboard-shortcut">
            Shift+{metaKeyName}+Enter
          </span>
        </MenubarItem>
      </MenubarSubmenu>
      <MenubarSubmenu
        id="classroom"
        title={t('Nav.Classroom.Title', { defaultValue: 'Classroom' })}
      >
        {isStaff && (
          <MenubarItem
            id="classroom-create"
            onClick={() => dispatch(openCreateClassroomModal())}
          >
            {t('Nav.Classroom.Create', { defaultValue: 'Create classroom' })}
          </MenubarItem>
        )}
        <MenubarItem
          id="classroom-join"
          onClick={() => dispatch(startJoinFlow())}
        >
          {t('Nav.Classroom.Join', { defaultValue: 'Join classroom' })}
        </MenubarItem>
        {isAdmin && (
          <MenubarItem
            id="classroom-add-role"
            onClick={() => dispatch(openAddRoleModal())}
          >
            {t('Nav.Classroom.AddRole', {
              defaultValue: 'Add Instructor or admin'
            })}
          </MenubarItem>
        )}
        {isStaff && (
          <MenubarItem
            id="classroom-managecode"
            onClick={() => dispatch(openManageJoinCodeModal())}
          >
            {t('Nav.Classroom.ManageCode', {
              defaultValue: 'Manage join code'
            })}
          </MenubarItem>
        )}
        {isStaff && (
          <MenubarItem
            id="classroom-publish"
            onClick={() => dispatch(openPublishClassroomModal())}
          >
            {t('Nav.Classroom.Publish', { defaultValue: 'Publish classroom' })}
          </MenubarItem>
        )}
        {isStaff && <MyClassroomMenu />}
        {isStaff && <ClassroomStudentsMenu />}
      </MenubarSubmenu>
      <MenubarSubmenu id="help" title={t('Nav.Help.Title')}>
        <MenubarItem
          id="help-shortcuts"
          onClick={() => dispatch(showKeyboardShortcutModal())}
        >
          {t('Nav.Help.KeyboardShortcuts')}
        </MenubarItem>
        <MenubarItem id="help-reference" href="https://p5js.org/reference/">
          {t('Nav.Help.Reference')}
        </MenubarItem>
        {/* The AstroTour[AR]-specific docs (AR workflow + the helper globals), served from
            /ar-reference — public, so no auth gate. */}
        <MenubarItem id="help-ar-reference" href="/ar-reference">
          {t('Nav.Help.ArReference', {
            defaultValue: 'AstroTour[AR] reference'
          })}
        </MenubarItem>
        <MenubarItem id="help-about" href="/about">
          {t('About.Title')}
        </MenubarItem>
        <MenubarItem
          id="help-report-bug"
          href="https://github.com/processing/p5.js-web-editor/issues/new/choose"
        >
          {t('Nav.Help.ReportBug')}
        </MenubarItem>
        <MenubarItem id="help-discord" href="https://discord.p5js.org">
          {t('Nav.Help.ChatOnDiscord')}
        </MenubarItem>
        <MenubarItem
          id="help-forum"
          href="https://discourse.processing.org/c/p5js/10"
        >
          {t('Nav.Help.PostOnTheForum')}
        </MenubarItem>
      </MenubarSubmenu>
      {parseBoolean(getConfig('TRANSLATIONS_ENABLED'), false) && (
        <LanguageMenu />
      )}
    </ul>
  );
};

const LanguageMenu = () => {
  const language = useSelector((state) => state.preferences.language);
  const dispatch = useDispatch();

  function handleLangSelection(event) {
    dispatch(setLanguage(event.target.value));
    dispatch(showToast('Toast.LangChange'));
  }

  return (
    <MenubarSubmenu
      id="lang"
      title={languageKeyToLabel(language)}
      triggerRole="button"
      listRole="listbox"
    >
      {sortBy(availableLanguages).map((key) => (
        // eslint-disable-next-line react/jsx-no-bind
        <MenubarItem
          id={key}
          key={key}
          value={key}
          onClick={handleLangSelection}
          role="option"
          selected={key === language}
        >
          {languageKeyToLabel(key)}
        </MenubarItem>
      ))}
    </MenubarSubmenu>
  );
};

const UnauthenticatedUserMenu = () => {
  const { t } = useTranslation();
  return (
    <ul className="nav__items-right" title="user-menu" role="group">
      <li className="nav__item">
        <Link to="/login" className="nav__auth-button">
          <span className="nav__item-header" title="Login">
            {t('Nav.Login')}
          </span>
        </Link>
      </li>
      <li className="nav__item-or" role="presentation">
        {t('Nav.LoginOr')}
      </li>
      <li className="nav__item">
        <Link to="/signup" className="nav__auth-button">
          <span className="nav__item-header" title="SignUp">
            {t('Nav.SignUp')}
          </span>
        </Link>
      </li>
    </ul>
  );
};

const AuthenticatedUserMenu = () => {
  const username = useSelector((state) => state.user.username);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <ul className="nav__items-right" title="user-menu" role="group">
      <MenubarSubmenu
        id="account"
        title={
          <span>
            {t('Nav.Auth.Hello')}, {username}!
          </span>
        }
      >
        <MenubarItem id="account-sketches" href={`/${username}/sketches`}>
          {t('Nav.Auth.MySketches')}
        </MenubarItem>
        <MenubarItem id="account-assets" href={`/${username}/assets`}>
          {t('Nav.Auth.MyAssets')}
        </MenubarItem>
        <MenubarItem id="account-settings" href="/account">
          {t('Nav.Auth.MyAccount')}
        </MenubarItem>
        <MenubarItem id="account-logout" onClick={() => dispatch(logoutUser())}>
          {t('Nav.Auth.LogOut')}
        </MenubarItem>
      </MenubarSubmenu>
    </ul>
  );
};

export default Nav;
